import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Debounced Nominatim-backed place search that auto-fills lat/lng.
 * onSelect receives { label, full, lat, lng }.
 */
export default function PlaceSearch({ value, onChange, onSelect, testid = "place-search" }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  // Keep input synced when parent updates the value programmatically.
  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const q = query.trim();
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (q.length < 2) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode", { params: { q, limit: 6 } });
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (r) => {
    skipNextFetchRef.current = true;   // <-- prevents the debounced effect from re-opening the dropdown
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery(r.label);
    setResults([]);
    setOpen(false);
    onChange?.(r.label);
    onSelect?.(r);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gold)]/70 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); }}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Start typing a town, city, country…"
          data-testid={testid}
          className="w-full h-11 pl-9 pr-9 rounded-lg bg-white border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)] focus:border-[color:var(--gold)] text-slate-900 font-sans-body text-sm font-medium placeholder:text-slate-500 placeholder:font-normal"
        />
        {loading ? (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--gold)]/70 animate-spin" />
        ) : (
          <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--gold)]/40" />
        )}
      </div>
      {open && results.length > 0 && (
        <ul
          className="absolute z-50 top-full left-0 right-0 mt-2 rounded-lg border border-[color:var(--gold)]/25 bg-[color:var(--bg-surface)] shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
          data-testid={`${testid}-results`}
        >
          {results.map((r, i) => (
            <li key={`${r.lat}-${r.lng}-${i}`}>
              <button
                type="button"
                onClick={() => pick(r)}
                data-testid={`${testid}-option-${i}`}
                className="w-full text-left px-4 py-3 hover:bg-[color:var(--gold)]/10 border-b border-[color:var(--gold)]/10 last:border-0"
                style={{ transition: "background-color 160ms ease" }}
              >
                <div className="text-[color:var(--parchment)] font-sans-body text-sm">{r.label}</div>
                <div className="text-[color:var(--text-muted)] font-serif-italic text-xs mt-0.5">
                  {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
